import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReviewsList from '@/components/reviews/ReviewsList';
import LoyaltyProgramManager from '@/components/loyalty/LoyaltyProgramManager';
import LoyaltyStatistics from '@/components/loyalty/LoyaltyStatistics';
import CouponOfferManager from '@/components/coupon/CouponOfferManager';
import RewardSystem from '@/components/loyalty/RewardSystem';
import ReviewInsights from '@/components/reviews/ReviewInsights';
import { AIFeedbackInsightsCard } from '@/components/reviews/AIFeedbackInsightsCard';
import { AIQuestionsStatsCard } from '@/components/reviews/AIQuestionsStatsCard';
import { FeedbackIntelligenceDashboard } from '@/components/reviews/FeedbackIntelligenceDashboard';
import DigitalWallet from '@/components/wallet/DigitalWallet';
import AppleWalletPassTab from '@/components/wallet/AppleWalletPassTab';
import QrCodeManager from '@/components/wallet/QrCodeManager';
import ReviewFormManager from '@/components/reviews/ReviewFormManager';
import GoogleReviewsTab from '@/components/reviews/GoogleReviewsTab';
import FilterControls from '@/components/analytics/FilterControls'; // Import FilterControls
import { ReviewCardsRow } from '@/components/reviews/ReviewCardsRow'; // Import ReviewCardsRow for all review cards
import BulkMailTab from '@/components/business/BulkMailTab';
import EmailTemplateManager from '@/components/business/EmailTemplateManager'; // Import EmailTemplateManager
import TimeTrackingTab from '@/components/payroll/TimeTrackingTab';
import GoogleCalendarIntegrationTab from '@/components/business/GoogleCalendarIntegrationTab';
import UnifiedRecruitmentTab from '@/components/recruitment/UnifiedRecruitmentTab';
import CustomersTab from '@/components/crm/CustomersTab';
import { SocialMediaTab } from '@/components/social';
import { SignageTab } from '@/components/signage';
import { Business, BusinessDetails, Review, ReviewSentiment, TimeDataPoint, SentimentDataPoint, SourceDataPoint } from '@/types/index';
import { useState, useEffect, useMemo } from 'react';
import { format } from "date-fns"; // Import format
import { supabase } from '@/integrations/supabase/client';
import { useGooglePlacesData } from '@/hooks/business/useGooglePlacesData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/ui/use-toast";
import { Database } from '@/integrations/supabase/types';
import { useReviewActions } from '@/hooks/analytics/useReviewActions'; // Import the new hook
import ReviewDetailsModal from '@/components/reviews/ReviewDetailsModal'; // Import ReviewDetailsModal
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"; // Import AlertDialog components
import { QrCodeData } from "@/types/qrCode"; // Import QrCodeData type

interface DashboardContentProps {
  business: BusinessDetails | null; // Accept full BusinessDetails or null from parent
  reviews: Review[]; // Accept reviews prop from parent
  onDeleteQrCode: (qrCodeId: string) => void;
  activeView: string;
  loadingData: boolean; // Add loadingData prop
  qrCodes: QrCodeData[]; // Add qrCodes prop
  setQrCodes: (qrCodes: QrCodeData[]) => void; // Add setQrCodes prop
  qrCodesLoading: boolean; // Add qrCodesLoading prop
  onBusinessCreated: (newBusiness: BusinessDetails) => void; // Add this prop
  onDeleteBusinessSuccess: () => void; // Add this prop
  onRefreshReviews: () => void; // Add refresh function prop
}

// Accept reviews as reviewsProp, business as businessDetailsProp
const DashboardContent: React.FC<DashboardContentProps> = ({
  business: businessDetailsProp,
  reviews: reviewsProp,
  onDeleteQrCode,
  activeView,
  loadingData, // Destructure loadingData
  qrCodes, // Destructure qrCodes
  setQrCodes, // Destructure setQrCodes
  qrCodesLoading, // Destructure qrCodesLoading
  onDeleteBusinessSuccess, // Destructure the new prop
  onBusinessCreated, // Destructure the onBusinessCreated prop
  onRefreshReviews, // Destructure the refresh function
 }) => {

  // State for insights derived from reviewsProp
  const [reviewsOverTimeData, setReviewsOverTimeData] = useState<TimeDataPoint[]>([]);
  const [sentimentAnalysisData, setSentimentAnalysisData] = useState<SentimentDataPoint[]>([]);
  const [reviewSourcesData, setReviewSourcesData] = useState<SourceDataPoint[]>([]);
  const [starRatingDistribution, setStarRatingDistribution] = useState<{ rating: number; count: number }[]>([]);
  const [trend, setTrend] = useState<'up' | 'down' | 'neutral' | null>(null);

  // State for managing review actions modals/dialogs
  const [isViewingReviewDetails, setIsViewingReviewDetails] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [showBulkDeleteConfirmation, setShowBulkDeleteConfirmation] = useState(false);
  const [reviewsToDelete, setReviewsToDelete] = useState<string[]>([]);

  // Use cached Google Places data hook instead of direct API call
  const {
    data: googlePlacesSummary,
    loading: loadingGooglePlacesSummary
  } = useGooglePlacesData(businessDetailsProp?.google_place_id);

  // State for filters
  const [selectedTag, setSelectedTag] = useState<string | "all">("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedSource, setSelectedSource] = useState<string | "all">("all");
  const [selectedSentiment, setSelectedSentiment] = useState<string | "all">("all");
  const [sortOption, setSortOption] = useState<string>("newest");

  // Use the custom hook for review actions
  const {
    handleViewDetails,
    handleDeleteReview,
    confirmDeleteReview,
    confirmBulkDeleteReviews,
  } = useReviewActions({
    onRefreshReviews,
    setIsViewingReviewDetails,
    setSelectedReview,
    setShowDeleteConfirmation,
    setReviewToDelete,
  });

  // Handler for bulk delete that shows confirmation dialog
  const handleBulkDelete = (reviewIds: string[]) => {
    setReviewsToDelete(reviewIds);
    setShowBulkDeleteConfirmation(true);
  };

  // Handler for confirming bulk delete
  const handleConfirmBulkDelete = async () => {
    if (reviewsToDelete.length > 0) {
      await confirmBulkDeleteReviews(reviewsToDelete);
      setShowBulkDeleteConfirmation(false);
      setReviewsToDelete([]);
    }
  };

  // Function to handle ONLY saving the reply text
  const handleSaveReply = async (reviewId: string, replyText: string) => {
    // Note: We still need a way to refetch the main reviews list if the parent doesn't handle it.
    const { error } = await supabase
      .from('reviews')
      .update({ reply: replyText })
      .eq('id', reviewId);

    if (error) {
      toast.error("Failed to save reply.", { description: error instanceof Error ? error.message : String(error), businessName: selectedReview?.businessName || businessDetailsProp?.name || "System" });
      throw error; // Re-throw error so the modal knows it failed
    } else {
      // TODO: Trigger review refetch here if possible.
      // Example: refetchReviews(); (assuming this function exists and is passed down)

      // Manually update the selectedReview state to reflect the change immediately in the modal
      if (selectedReview && selectedReview.id === reviewId) {
        setSelectedReview({ ...selectedReview, reply: replyText });
      }
      // Do not send email here anymore
    }
  };

  // Function to handle sending the reply email via Edge Function
  const handleSendReplyEmail = async (review: Review, replyText: string) => {
    if (!review.customerEmail) {
      toast.error("Cannot send email: Customer email is missing.", { businessName: review.businessName || businessDetailsProp?.name || "System" });
      throw new Error("Customer email missing"); // Throw error to indicate failure
    }
    if (!review.business_id) {
       toast.error("Cannot send email: Business ID missing from review data.", { businessName: review.businessName || businessDetailsProp?.name || "System" });
       throw new Error("Business ID missing");
    }

    try {
      const { error: functionError } = await supabase.functions.invoke('send-reply-email', {
        body: {
          customerEmail: review.customerEmail,
          businessId: review.business_id,
          reviewText: review.text,
          replyText: replyText,
          reviewId: review.id
        }
      });

      if (functionError) {
        toast.error("Failed to send email notification.", { description: functionError.message, businessName: review.businessName || businessDetailsProp?.name || "System" });
        throw functionError; // Re-throw error
      } else {
        toast.success("Reply email notification sent to customer.", { businessName: review.businessName || businessDetailsProp?.name || "System" });
        // Update local state to reflect email sent status immediately
        if (selectedReview && selectedReview.id === review.id) {
          setSelectedReview({ ...selectedReview, reply_email_sent_at: new Date().toISOString() });
        }
        // TODO: Ideally trigger a refetch of the main reviews list as well
      }
    } catch (invokeError: any) {
       toast.error("An error occurred trying to send the email notification.", { description: invokeError.message, businessName: review.businessName || businessDetailsProp?.name || "System" });
       throw invokeError; // Re-throw error
    }
  };


  // Calculate unique tags from the reviews prop
  const uniqueTags = useMemo(() => {
    const tags = (reviewsProp || [])
      .map(review => review.tagName)
      .filter((tagName): tagName is string => tagName !== null && tagName !== undefined);
    return ["all", ...Array.from(new Set(tags))];
  }, [reviewsProp]);

  // Filter reviews based on selected filters using the reviews prop
  const filteredReviews = useMemo(() => {
    let filtered = reviewsProp || [];

    if (selectedTag !== "all") {
      filtered = filtered.filter(review => review.tagName === selectedTag);
    }
    if (startDate) {
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      filtered = filtered.filter(review => new Date(review.created_at) >= startOfDay);
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(review => new Date(review.created_at) <= endOfDay);
    }
    if (selectedSource !== "all") {
      filtered = filtered.filter(review => review.source === selectedSource);
    }
    if (selectedSentiment !== "all") {
      filtered = filtered.filter(review => {
        let reviewSentiment: ReviewSentiment = 'neutral';
        if (review.rating !== undefined && review.rating !== null) {
          if (review.rating >= 4) reviewSentiment = 'positive';
          else if (review.rating <= 2) reviewSentiment = 'negative';
        } else if (review.sentiment) {
           reviewSentiment = review.sentiment;
        }
        return reviewSentiment === selectedSentiment;
      });
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortOption) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'most_stars':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'least_stars':
        sorted.sort((a, b) => a.rating - b.rating);
        break;
      default:
        sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return sorted;
  }, [reviewsProp, selectedTag, startDate, endDate, selectedSource, selectedSentiment, sortOption]);

  // Generate filter description based on current filters
  const filterDescription = useMemo(() => {
    const descriptions: string[] = [];
    if (selectedSentiment !== "all") descriptions.push(`${selectedSentiment.charAt(0).toUpperCase() + selectedSentiment.slice(1)} Reviews`);
    else descriptions.push("Recent Reviews");
    if (selectedTag !== "all") descriptions.push(`tagged "${selectedTag}"`);
    if (selectedSource !== "all") descriptions.push(`from ${selectedSource.charAt(0).toUpperCase() + selectedSource.slice(1)}`);
    if (startDate && endDate) descriptions.push(`between ${startDate.toLocaleDateString()} and ${endDate.toLocaleDateString()}`);
    else if (startDate) descriptions.push(`since ${startDate.toLocaleDateString()}`);
    else if (endDate) descriptions.push(`until ${endDate.toLocaleDateString()}`);
    return descriptions.join(" ");
  }, [selectedTag, startDate, endDate, selectedSource, selectedSentiment]);

  // Effect to process reviews data for insights based on reviewsProp
  useEffect(() => {
    const tagInnReviews = reviewsProp || [];
    
    if (tagInnReviews.length === 0 && !googlePlacesSummary) {
      setSentimentAnalysisData([]);
      setReviewSourcesData([]);
      setReviewsOverTimeData([]);
      setStarRatingDistribution([]);
      setTrend(null);
      return;
    }

    // Calculate Sentiment Analysis Data (using only Tag-Inn reviews since Google doesn't provide detailed sentiment)
    const sentimentCounts: { [key: string]: number } = { positive: 0, neutral: 0, negative: 0 };
    tagInnReviews.forEach(review => {
      let reviewSentiment: ReviewSentiment = 'neutral';
      if (review.rating !== undefined && review.rating !== null) {
        if (review.rating >= 4) reviewSentiment = 'positive';
        else if (review.rating <= 2) reviewSentiment = 'negative';
      } else if (review.sentiment) {
         reviewSentiment = review.sentiment;
      }
      sentimentCounts[reviewSentiment] = (sentimentCounts[reviewSentiment] || 0) + 1;
    });
    const sentimentData: SentimentDataPoint[] = Object.keys(sentimentCounts)
      .filter(key => sentimentCounts[key] > 0)
      .map(key => ({
        name: key.charAt(0).toUpperCase() + key.slice(1) as "Positive" | "Neutral" | "Negative",
        value: sentimentCounts[key],
        color: key === 'positive' ? '#22c55e' : key === 'negative' ? '#ef4444' : '#94a3b8',
      }));
    setSentimentAnalysisData(sentimentData);

    // Calculate Review Sources Data
    const sourceCounts: { [key: string]: number } = {};
    
    // Add Tag-Inn reviews
    tagInnReviews.forEach(review => {
      const sourceName = review.source || 'Tag-Inn';
      sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
    });
    
    // Keep only Tag-Inn sources in the analytics
    
    const sourceData: SourceDataPoint[] = Object.keys(sourceCounts).map(key => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value: sourceCounts[key],
      color: key === 'Google' ? '#4285F4' : key === 'Internal' ? '#8b5cf6' : '#60a5fa',
    }));
    setReviewSourcesData(sourceData);

    // Calculate Reviews Over Time Data (Daily) - using only Tag-Inn reviews with dates
    const reviewsByDay: { [key: string]: { positive: number; negative: number; neutral: number } } = {};
    tagInnReviews.forEach(review => {
      if (review.created_at) {
        const date = new Date(review.created_at);
        // Format as YYYY-MM-DD for daily aggregation
        const dayMonthYear = format(date, 'yyyy-MM-dd');
        if (!reviewsByDay[dayMonthYear]) reviewsByDay[dayMonthYear] = { positive: 0, negative: 0, neutral: 0 };
        let reviewSentiment: ReviewSentiment = 'neutral';
        if (review.rating !== undefined && review.rating !== null) {
          if (review.rating >= 4) reviewSentiment = 'positive';
          else if (review.rating <= 2) reviewSentiment = 'negative';
        } else if (review.sentiment) {
           reviewSentiment = review.sentiment;
        }
        reviewsByDay[dayMonthYear][reviewSentiment]++;
      }
    });
    const timeData: TimeDataPoint[] = Object.keys(reviewsByDay)
      .map(date => ({
        date: date,
        positive: reviewsByDay[date].positive || 0,
        negative: reviewsByDay[date].negative || 0,
        neutral: reviewsByDay[date].neutral || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    setReviewsOverTimeData(timeData);

    // Calculate Star Rating Distribution (using only Tag-Inn reviews for detailed breakdown)
    const ratingCounts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    tagInnReviews.forEach(review => {
      if (review.rating >= 1 && review.rating <= 5) ratingCounts[review.rating]++;
    });
    const distributionData = [5, 4, 3, 2, 1].map(rating => ({
      rating: rating,
      count: ratingCounts[rating] || 0,
    }));
    setStarRatingDistribution(distributionData);

    // Calculate Trend (using only Tag-Inn reviews for trend analysis)
    const sortedReviews = [...tagInnReviews].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const recentReviews = sortedReviews.slice(0, 10);
    const olderReviews = sortedReviews.slice(10, 20);
    if (recentReviews.length === 0) setTrend(null);
    else if (olderReviews.length === 0) setTrend(tagInnReviews.length < 20 ? 'neutral' : null);
    else {
      const recentAverage = recentReviews.reduce((sum, r) => sum + r.rating, 0) / recentReviews.length;
      const olderAverage = olderReviews.reduce((sum, r) => sum + r.rating, 0) / olderReviews.length;
      const difference = recentAverage - olderAverage;
      const trendThreshold = 0.2;
      if (difference > trendThreshold) setTrend('up');
      else if (difference < -trendThreshold) setTrend('down');
      else setTrend('neutral');
    }
  }, [reviewsProp, googlePlacesSummary]);

  // Removed the useEffect that fetched business details and reviews internally
  // Removed the useEffect for realtime subscription


  // Use businessDetailsProp directly
  const currentBusinessDetails = businessDetailsProp;

  // Filtered data for insights based on the filtered reviews
  // This recalculates insights based on the currently filtered reviews
  // Moved this hook before the early return to fix hook order error
  const filteredInsightsData = useMemo(() => {
     const sentimentCounts: { [key: string]: number } = { positive: 0, neutral: 0, negative: 0 };
     filteredReviews.forEach(review => {
       let reviewSentiment: ReviewSentiment = 'neutral';
       if (review.rating !== undefined && review.rating !== null) {
         if (review.rating >= 4) reviewSentiment = 'positive';
         else if (review.rating <= 2) reviewSentiment = 'negative';
       } else if (review.sentiment) {
          reviewSentiment = review.sentiment;
       }
       sentimentCounts[reviewSentiment]++;
     });
     const sentimentData: SentimentDataPoint[] = Object.keys(sentimentCounts)
       .filter(key => sentimentCounts[key] > 0)
       .map(key => ({
         name: key.charAt(0).toUpperCase() + key.slice(1) as "Positive" | "Neutral" | "Negative",
         value: sentimentCounts[key],
         color: key === 'positive' ? '#22c55e' : key === 'negative' ? '#ef4444' : '#94a3b8',
       }));

     const sourceCounts: { [key: string]: number } = {};
     filteredReviews.forEach(review => {
       const sourceName = review.source || 'Unknown';
       sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
     });
     const sourceData: SourceDataPoint[] = Object.keys(sourceCounts).map(key => ({
       name: key.charAt(0).toUpperCase() + key.slice(1),
       value: sourceCounts[key],
       color: key === 'Google' ? '#4285F4' : key === 'Internal' ? '#8b5cf6' : '#60a5fa',
     }));

     const ratingCounts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
     filteredReviews.forEach(review => {
       if (review.rating >= 1 && review.rating <= 5) ratingCounts[review.rating]++;
     });
     const distributionData = [5, 4, 3, 2, 1].map(rating => ({
       rating: rating,
       count: ratingCounts[rating] || 0,
     }));

     // Calculate Tag-Inn reviews only
     const totalReviewsCount = filteredReviews.length;
     const totalRatingSum = filteredReviews.reduce((sum, review) => sum + review.rating, 0);
     const averageRating = totalReviewsCount > 0 ? totalRatingSum / totalReviewsCount : null;

     // Trend calculation based on filtered data (might be less accurate)
     const calculatedTrend: 'up' | 'down' | 'neutral' | null = trend; // Default to overall trend calculated from reviewsProp

     // Filter reviewsOverTimeData based on date range
     let filteredTimeData = reviewsOverTimeData;
     if (startDate) {
       const startOfDay = new Date(startDate);
       startOfDay.setHours(0, 0, 0, 0);
       filteredTimeData = filteredTimeData.filter(item => new Date(item.date) >= startOfDay);
     }
     if (endDate) {
       const endOfDay = new Date(endDate);
       endOfDay.setHours(23, 59, 59, 999);
       filteredTimeData = filteredTimeData.filter(item => new Date(item.date) <= endOfDay);
     }


     return {
       sentimentAnalysisData: sentimentData,
       reviewSourcesData: sourceData,
       starRatingDistribution: distributionData,
       averageRating: averageRating,
       totalReviews: totalReviewsCount,
       trend: calculatedTrend,
       reviewsOverTimeData: filteredTimeData, // Use the date-filtered time data
     };
   }, [filteredReviews, reviewsOverTimeData, trend, startDate, endDate]); // Add date filters

  // If no business data yet, show nothing (handled by parent)
  if (!currentBusinessDetails) {
    return null;
  }


  return (
    <div className="flex flex-col gap-4">
      
      {/* Conditionally render content based on activeView */}
      {activeView === 'reviews' && (
        <>
          <ReviewCardsRow
            currentAverage={filteredInsightsData.averageRating || 0}
            totalReviews={filteredInsightsData.totalReviews || 0}
            sentimentData={filteredInsightsData.sentimentAnalysisData || []}
            latestReview={filteredReviews.length > 0 ? filteredReviews[0] : null}
            onViewDetails={handleViewDetails}
          />
          <ReviewsList
            reviews={filteredReviews} // Pass the filtered reviews
            filterDescription={filterDescription}
            onViewDetails={handleViewDetails}
            onDeleteReview={handleDeleteReview} // Pass delete handler
            onBulkDelete={handleBulkDelete} // Pass bulk delete handler with confirmation
            businessId={currentBusinessDetails?.id}
            countryCode={currentBusinessDetails?.country_code}
            filterControls={
              <FilterControls
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedSource={selectedSource}
                setSelectedSource={setSelectedSource}
                reviewSourcesData={filteredInsightsData.reviewSourcesData} // Use filtered insight data
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                uniqueTags={uniqueTags}
                selectedSentiment={selectedSentiment}
                setSelectedSentiment={setSelectedSentiment}
                sortOption={sortOption}
                setSortOption={setSortOption}
              />
            }
          />
        </>
      )}

      {activeView === 'insights' && (
        <>
           <FilterControls // Keep filters for insights view as well
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            reviewSourcesData={filteredInsightsData.reviewSourcesData} // Use filtered insight data
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            uniqueTags={uniqueTags}
            selectedSentiment={selectedSentiment}
            setSelectedSentiment={setSelectedSentiment}
            sortOption={sortOption} // Pass sort option if needed for display/consistency
            setSortOption={setSortOption} // Pass setter if needed
          />
          <ReviewInsights
            reviewsOverTimeData={filteredInsightsData.reviewsOverTimeData} // Use filtered time data
            sentimentAnalysisData={filteredInsightsData.sentimentAnalysisData}
            reviewSourcesData={filteredInsightsData.reviewSourcesData}
            averageRating={filteredInsightsData.averageRating}
            totalReviews={filteredInsightsData.totalReviews}
            starRatingDistribution={filteredInsightsData.starRatingDistribution}
            trend={filteredInsightsData.trend}
            businessDetails={businessDetailsProp}
          />

          {/* AI-Powered Insights */}
          {currentBusinessDetails?.id && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <AIFeedbackInsightsCard businessId={currentBusinessDetails.id} />
              <AIQuestionsStatsCard businessId={currentBusinessDetails.id} />
            </div>
          )}
        </>
      )}

      {activeView === 'feedback-intelligence' && currentBusinessDetails?.id && (
        <FeedbackIntelligenceDashboard businessId={currentBusinessDetails.id} />
      )}

      {activeView === 'qrcodes' && (
        <QrCodeManager
          selectedBusinessId={currentBusinessDetails.id} // Use currentBusinessDetails
          qrCodes={qrCodes}
          setQrCodes={setQrCodes} // Keep internal state management for QR codes for now
          onDeleteQrCode={onDeleteQrCode}
          loading={qrCodesLoading}
        />
      )}

      {activeView === 'forms' && (
        <ReviewFormManager businessId={currentBusinessDetails.id} />
      )}

      {activeView === 'loyalty' && (
        <LoyaltyProgramManager businessId={currentBusinessDetails?.id || null} />
      )}

      {activeView === 'loyalty-statistics' && (
        <LoyaltyStatistics businessId={currentBusinessDetails?.id || null} />
      )}

      {activeView === 'coupons' && (
        <CouponOfferManager businessId={currentBusinessDetails?.id || null} />
      )}

      {activeView === 'google' && (
        <GoogleReviewsTab business={currentBusinessDetails} />
      )}

      

      {activeView === 'bulk-mail' && (
        <BulkMailTab />
      )}

      {activeView === 'email-templates' && (
        <EmailTemplateManager businessId={currentBusinessDetails.id} />
      )}

      {activeView === 'time-tracking' && (
        <TimeTrackingTab business={currentBusinessDetails} loadingData={loadingData} />
      )}

      {activeView === 'google-calendar' && (
        <GoogleCalendarIntegrationTab 
          businessId={currentBusinessDetails.id}
          businessName={currentBusinessDetails.name}
          countryCode={currentBusinessDetails.country_code}
          shifts={[]} // Will need to be populated with actual shift data
          onShiftCreated={() => {}}
        />
      )}

      {activeView === 'job-postings' && (
        <UnifiedRecruitmentTab businessId={currentBusinessDetails.id} />
      )}

      {activeView === 'customers' && (
        <CustomersTab businessId={currentBusinessDetails.id} />
      )}

      {/* Social Media Views */}
      {(activeView === 'social-accounts' ||
        activeView === 'social-posts' ||
        activeView === 'social-inbox' ||
        activeView === 'social-analytics' ||
        activeView === 'social-auto-reply') && (
        <SocialMediaTab
          businessId={currentBusinessDetails?.id || null}
          activeView={activeView}
        />
      )}

      {/* Digital Signage View */}
      {activeView === 'signage' && currentBusinessDetails && (
        <SignageTab businessId={currentBusinessDetails.id} />
      )}

      {/* Modals and Dialogs (remain outside conditional rendering) */}
      <ReviewDetailsModal
        isOpen={isViewingReviewDetails}
        onClose={() => setIsViewingReviewDetails(false)}
        review={selectedReview}
        onSaveReply={handleSaveReply}
        onSendReplyEmail={handleSendReplyEmail} // Pass the new email handler function
      />

      <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteConfirmation(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (reviewToDelete) {
                confirmDeleteReview(reviewToDelete); // This might need adjustment based on hook refactor
                setReviewToDelete(null);
              }
              setShowDeleteConfirmation(false);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBulkDeleteConfirmation} onOpenChange={setShowBulkDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Reviews</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {reviewsToDelete.length} review{reviewsToDelete.length !== 1 ? 's' : ''}? 
              This action cannot be undone and will permanently delete all selected reviews.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowBulkDeleteConfirmation(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {reviewsToDelete.length} Review{reviewsToDelete.length !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default DashboardContent;
